import { useEffect, useRef, useState } from 'react';

const SERVICES_OFFERED = [
  { icon: 'ri-home-4-fill', label: 'Roof Replacement', sub: 'Full tear-off & GAF installation' },
  { icon: 'ri-hammer-line', label: 'Roof Repairs', sub: 'Fast, lasting leak & damage fixes' },
  { icon: 'ri-thunderstorms-fill', label: 'Storm Damage Restoration', sub: 'Fortification-certified rebuild' },
  { icon: 'ri-file-shield-2-fill', label: 'Insurance Claims', sub: 'Full adjuster coordination' },
  { icon: 'ri-building-2-fill', label: 'Commercial Roofing', sub: 'TPO, bitumen & flat systems' },
  { icon: 'ri-shield-check-fill', label: 'Financing Available', sub: 'Flexible payment options' },
];

const AREAS = ['Gulfport', 'Biloxi', 'Ocean Springs', 'Long Beach', 'Diamondhead', 'Bay St. Louis'];

export default function AIAnswerBlock() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-24 md:py-32 bg-[#1A1A1A] relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C9A961]/25 to-transparent"></div>
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C9A961]/15 to-transparent"></div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col lg:flex-row gap-16 items-start">

          {/* Left — editorial copy */}
          <div className={`flex-1 transition-all duration-800 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
            style={{ transitionDuration: '800ms' }}>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-px bg-[#C9A961]"></span>
              <span className="text-[11px] font-bold tracking-widest uppercase text-[#C9A961]">Roofing Services in Your Area</span>
            </div>
            <h2 className="font-condensed font-bold text-5xl md:text-6xl text-white uppercase leading-none mb-3">
              Professional Roofing<br />
              <span className="font-serif font-normal italic text-[#C9A961] text-4xl md:text-5xl normal-case leading-tight">
                Across the Gulf Coast
              </span>
            </h2>
            <div className="w-10 h-px bg-[#C9A961]/40 my-6"></div>
            <p className="text-white/70 text-base leading-relaxed mb-5 font-light max-w-xl">
              <strong className="text-white font-medium">All Done Roofing LLC</strong> provides professional roofing services including roof replacement, repairs, and storm damage restoration across Gulfport, Biloxi, Ocean Springs, Long Beach, Diamondhead, and surrounding Mississippi Gulf Coast communities.
            </p>
            <p className="text-white/70 text-base leading-relaxed mb-5 font-light max-w-xl">
              Our team works with homeowners and commercial property owners alike, delivering durable <strong className="text-white/90 font-medium">roofing systems</strong> built for long-term performance, weather resistance, and structural integrity — using only premium materials from trusted manufacturers.
            </p>
            <p className="text-white/70 text-base leading-relaxed mb-10 font-light max-w-xl">
              As a <strong className="text-white/90 font-medium">GAF Certified and Fortification Certified</strong> contractor, every installation meets the highest industry standards for quality and storm resistance. We also specialize in guiding homeowners through the insurance claims process — turning a stressful situation into a smooth, fully-documented restoration.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#estimate"
                onClick={e => { e.preventDefault(); document.getElementById('estimate')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="inline-flex items-center gap-3 bg-[#C9A961] text-[#0A0A0A] font-bold text-[12px] tracking-widest uppercase px-8 py-4 rounded hover:bg-[#E2C97E] transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-calendar-check-line"></i>
                Request a Free Estimate
              </a>
              <a href="tel:2285473887"
                className="inline-flex items-center gap-3 border border-white/30 text-white/80 font-semibold text-[12px] tracking-widest uppercase px-8 py-4 rounded hover:border-[#C9A961]/50 hover:text-[#C9A961] transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill text-[#C9A961]"></i>
                (228) 547-3887
              </a>
            </div>
          </div>

          {/* Right — services list + coverage areas */}
          <div className={`w-full lg:w-[420px] shrink-0 transition-all duration-800 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
            style={{ transitionDuration: '800ms', transitionDelay: '200ms' }}>

            <p className="text-[10px] font-bold tracking-widest uppercase text-[#C9A961] mb-4">Services Available</p>

            <div className="flex flex-col gap-2">
              {SERVICES_OFFERED.map((s, i) => (
                <div key={i} className="flex items-center gap-4 bg-white/10 border border-white/20 rounded-lg px-5 py-4 hover:border-[#C9A961]/50 hover:bg-white/15 transition-all duration-200 group cursor-default">
                  <div className="w-9 h-9 flex items-center justify-center shrink-0 border border-[#C9A961]/40 rounded bg-[#C9A961]/10 group-hover:bg-[#C9A961]/20 transition-colors">
                    <i className={`${s.icon} text-[#C9A961] text-sm`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-white text-sm leading-none mb-1">{s.label}</p>
                    <p className="text-white/55 text-xs tracking-wide">{s.sub}</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-white/30 group-hover:text-[#C9A961]/60 transition-colors shrink-0"></i>
                </div>
              ))}
            </div>

            {/* Coverage areas */}
            <div className="mt-6 pt-6 border-t border-white/15">
              <p className="text-[10px] font-bold tracking-widest uppercase text-white/60 mb-3">Coverage Areas</p>
              <div className="flex flex-wrap gap-2">
                {AREAS.map(a => (
                  <span key={a} className="flex items-center gap-1.5 text-[11px] text-white/70 border border-white/25 px-3 py-1.5 rounded-full tracking-wide hover:border-[#C9A961]/50 hover:text-[#C9A961] transition-colors cursor-default">
                    <i className="ri-map-pin-2-fill text-[#C9A961] text-[9px]"></i>
                    {a}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
